import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { subscriptionsApi, sitesApi, pagesApi, layoutsApi } from '../../api/client';
import { SiteSEOOverviewPanel } from '../../components/SiteSEOOverviewPanel';
import { CreatePageModal } from '../../components/CreatePageModal';
import { EditPageModal } from '../../components/EditPageModal';
import { DeleteConfirmModal } from '../../components/DeleteConfirmModal';
import { ConfirmModal } from '../../components/ConfirmModal';
import { SaveSiteAsTemplateModal } from '../../components/SaveSiteAsTemplateModal';
import { PublishConfirmModal } from '../../components/PublishConfirmModal';
import { ResponsivePreviewPanel } from '../../components/ResponsivePreviewPanel';
import { MediaLibraryModal } from '../../components/MediaLibraryModal';
import { BrandPackageManager } from '../../components/BrandPackageManager';
import { useSiteManager } from './useSiteManager';
import { ThemeSelectorModal } from './ThemeSelectorModal';
import { SeoSettingsModal } from './SeoSettingsModal';
import { ToastNotification } from './ToastNotification';
import { SiteManagerHeader } from './SiteManagerHeader';
import { WebsiteInfoSection } from './WebsiteInfoSection';
import { PagesSection } from './PagesSection';
import { LayoutsSection } from './LayoutsSection';
import { AppsSection } from './AppsSection';
import { brandPackagesApi, type BrandPackage } from '../../api/modules/brandPackages';
import type { Page, PageLayout } from '../../schemas/validation';
import { useUnpublishedSummary } from '../../hooks/useUnpublishedSummary';
import { RoaryAddPageFlow } from '../../components/Roary';
import { canUseRoary } from '../../utils/roaryAccess';
import { GettingStartedHost } from '../../components/GettingStarted';

export const SiteManager: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Local state for Media Library modal
  const [showMediaLibrary, setShowMediaLibrary] = React.useState(false);
  // Quick-publish state
  const [publishingPageId, setPublishingPageId] = React.useState<string | null>(null);
  const [publishingLayoutId, setPublishingLayoutId] = React.useState<string | null>(null);
  const [isPublishingAll, setIsPublishingAll] = React.useState(false);
  const [showSeoPublishPrompt, setShowSeoPublishPrompt] = React.useState(false);
  // Brand package state
  const [showBrandPackageModal, setShowBrandPackageModal] = React.useState(false);
  const [selectedBrandPackage, setSelectedBrandPackage] = React.useState<BrandPackage | null>(null);
  const [_isUpdatingBrandPackage, setIsUpdatingBrandPackage] = React.useState(false);
  const [showRoaryAddPage, setShowRoaryAddPage] = React.useState(false);

  const {
    siteId,
    navigate,
    isMimicking,
    isAdmin,
    site,
    setSite,
    pages,
    isLoading,
    showCreatePageModal,
    setShowCreatePageModal,
    isCreatingPage,
    showEditPageModal,
    pageToEdit,
    showDeleteModal,
    pageToDelete,
    isDeletingPage,
    canCreatePage,
    sensors,
    isReordering,
    activeId,
    activePage,
    showSaveTemplateModal,
    setShowSaveTemplateModal,
    isSavingTemplate,
    showPublishModal,
    isPublishing,
    showSuccessToast,
    setShowSuccessToast,
    successMessage,
    toastType,
    showToast,
    showThemeSelector,
    setShowThemeSelector,
    showResponsivePreview,
    setShowResponsivePreview,
    showSeoModal,
    setShowSeoModal,
    showSEOOverview,
    setShowSEOOverview,
    blogData,
    isBlogLoading,
    isInstallingBlog,
    showBlogUninstallConfirm,
    setShowBlogUninstallConfirm,
    isUninstallingBlog,
    layouts,
    isLayoutsLoading,
    layoutToDelete,
    setLayoutToDelete,
    showLayoutDeleteConfirm,
    setShowLayoutDeleteConfirm,
    isDeletingLayout,
    handleDeleteLayout,
    handleInstallBlog,
    handleUninstallBlog,
    handleDragStart,
    handleDragEnd,
    handleCreatePage,
    handleCreatePageFromTemplate,
    handleEditPageClick,
    handleEditPageConfirm,
    handleEditPageCancel,
    handleDeletePageClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleToggleHomePage,
    handleSaveAsTemplate,
    handlePublishSiteClick,
    handlePublishSiteConfirm,
    handlePublishModalClose,
    formatDate,
    loadLayoutsData,
  } = useSiteManager();

  const showRoaryUi = canUseRoary(isAdmin, isMimicking);

  // ─── Unpublished summary (single source of truth for badges + global count) ──
  const {
    totalChanged,
    hasChanges,
    isLoading: isSummaryLoading,
    refetch: refetchSummary,
    markLayoutSynced,
    pageStatusMap,
    layoutStatusMap,
  } = useUnpublishedSummary(siteId);

  // Open publish modal when arriving from Get Started (`?publish=1`).
  React.useEffect(() => {
    if (searchParams.get('publish') !== '1' || isLoading || !site) return;
    handlePublishSiteClick();
    const next = new URLSearchParams(searchParams);
    next.delete('publish');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, isLoading, site, handlePublishSiteClick]);

  // Load brand package when site has one assigned
  const siteBrandPackageId = (site as any)?.brandPackageId as string | undefined;
  React.useEffect(() => {
    const loadBrandPackage = async () => {
      if (siteBrandPackageId) {
        try {
          const bp = await brandPackagesApi.get(siteBrandPackageId);
          setSelectedBrandPackage(bp);
        } catch (error) {
          console.error('Failed to load brand package:', error);
          setSelectedBrandPackage(null);
        }
      } else {
        setSelectedBrandPackage(null);
      }
    };
    loadBrandPackage();
  }, [siteBrandPackageId]);

  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-12 h-12 mx-auto mb-4 spinner"></div>
          <p className="text-gray-600 font-medium">Loading website...</p>
        </div>
      </div>
    );
  }

  if (!site) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <h2 className="text-2xl font-bold text-gray-900">Website not found</h2>
          <p className="mt-2 text-gray-600">
            The website you're looking for doesn't exist or you don't have access to it.
          </p>
          <Link to="/dashboard" className="mt-4 btn-primary inline-block">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const handleAddPageClick = async () => {
    if (!canCreatePage) {
      const { canCreate, reason } = await subscriptionsApi.canCreatePage(siteId!);
      if (!canCreate) {
        showToast(
          reason ||
            'You have reached the page limit for your plan. Please upgrade to create more pages.',
          'error',
          5000
        );
        return;
      }
    }
    setShowCreatePageModal(true);
  };

  const handleAddPageWithRoaryClick = async () => {
    if (!canCreatePage) {
      const { canCreate, reason } = await subscriptionsApi.canCreatePage(siteId!);
      if (!canCreate) {
        showToast(
          reason ||
            'You have reached the page limit for your plan. Please upgrade to create more pages.',
          'error',
          5000
        );
        return;
      }
    }
    setShowRoaryAddPage(true);
  };

  // Handle brand package selection
  const handleBrandPackageSelect = async (brandPackage: BrandPackage | null) => {
    if (!siteId) return;

    try {
      setIsUpdatingBrandPackage(true);
      const updatedSite = await sitesApi.update(siteId, {
        brandPackageId: brandPackage?.id || null,
      });
      setSite(updatedSite);
      setSelectedBrandPackage(brandPackage);
      showToast(
        brandPackage
          ? `Brand package "${brandPackage.name}" applied successfully!`
          : 'Switched to theme colors',
        'success'
      );
      // Refetch unpublished summary so publish badges update immediately
      await refetchSummary();
    } catch (error) {
      console.error('Failed to update brand package:', error);
      showToast('Failed to update brand package. Please try again.', 'error');
    } finally {
      setIsUpdatingBrandPackage(false);
    }
  };

  // Handle publish all from the header
  const handlePublishAll = async () => {
    if (!siteId || isPublishingAll) return;

    setIsPublishingAll(true);
    try {
      const result = await sitesApi.publishAll(siteId);
      const { layouts: lr, blogPosts: bp, pages: pr } = result.results;

      // Build success message
      const successParts: string[] = [];
      if (pr.published > 0) successParts.push(`${pr.published} page(s)`);
      if (lr.published > 0) successParts.push(`${lr.published} layout(s)`);
      if (bp.published > 0) successParts.push(`${bp.published} blog post(s)`);

      // Collect all failures with human-readable titles
      const allFailed = [...(pr.failed || []), ...(lr.failed || []), ...(bp.failed || [])];

      let message =
        successParts.length > 0
          ? `Published ${successParts.join(', ')}`
          : 'Everything is already up to date';

      if (allFailed.length > 0) {
        const failedNames = allFailed.map(f => `"${f.title}"`).join(', ');
        message += `. Failed: ${failedNames}`;
      }

      showToast(
        message,
        allFailed.length > 0 ? 'error' : 'success',
        allFailed.length > 0 ? 8000 : 3000
      );

      // Refetch summary to reflect new state
      await refetchSummary();
    } catch (err: any) {
      console.error('Publish all failed:', err);
      showToast(err?.message || 'Failed to publish changes', 'error');
    } finally {
      setIsPublishingAll(false);
      setShowSeoPublishPrompt(false);
    }
  };

  // Handle quick-publish for a layout from the list
  const handleQuickPublishLayout = async (layout: PageLayout) => {
    setPublishingLayoutId(layout.id);
    try {
      const result = await layoutsApi.publish(layout.id);
      const parts: string[] = [`"${layout.name}" published`];
      if (result.republishedPageIds?.length) {
        parts.push(`${result.republishedPageIds.length} page(s) republished`);
      }
      if (result.skippedNeverPublishedPageIds?.length) {
        parts.push(`${result.skippedNeverPublishedPageIds.length} draft-only page(s) not affected`);
      }
      showToast(parts.join('. '), 'success');
      await refetchSummary();
      // Ensure rocket/badges clear even if summary lags or omitted a row (see API unpublished-summary layouts loop)
      markLayoutSynced(layout.id, layout.name);
      void loadLayoutsData();
    } catch (err: any) {
      showToast(err?.message || 'Failed to publish layout', 'error');
    } finally {
      setPublishingLayoutId(null);
    }
  };

  // Handle quick-publish for a single page from the list
  const handleQuickPublishPage = async (page: Page) => {
    setPublishingPageId(page.id);
    try {
      await pagesApi.publish(page.id);
      showToast(`"${page.title}" published successfully`, 'success');
      // Refetch summary so global count and badges update
      await refetchSummary();
    } catch (err: any) {
      showToast(err?.message || 'Failed to publish page', 'error');
    } finally {
      setPublishingPageId(null);
    }
  };

  return (
    <div className={`page-container ${isMimicking ? 'mt-[52px]' : ''}`}>
      <SiteManagerHeader
        site={site}
        pages={pages}
        siteId={siteId!}
        isSavingTemplate={isSavingTemplate}
        formatDate={formatDate}
        onSaveTemplateClick={() => setShowSaveTemplateModal(true)}
        onSettingsClick={() => navigate(`/sites/${siteId}/settings`)}
        onSEOOverviewClick={() => setShowSEOOverview(true)}
        onPreviewClick={() => pages.length > 0 && setShowResponsivePreview(true)}
        onMediaLibraryClick={() => setShowMediaLibrary(true)}
        onAnalyticsClick={() => navigate(`/sites/${siteId}/analytics`)}
        onSubmissionsClick={() => navigate(`/sites/${siteId}/submissions`)}
        totalChanged={totalChanged}
        hasChanges={hasChanges}
        isSummaryLoading={isSummaryLoading}
        isPublishingAll={isPublishingAll}
        onPublishAll={handlePublishAll}
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <WebsiteInfoSection
            site={site}
            onThemeClick={() => setShowThemeSelector(true)}
            onSeoClick={() => setShowSeoModal(true)}
            onBrandPackageClick={() => setShowBrandPackageModal(true)}
            brandPackageName={selectedBrandPackage?.name}
            onPublishClick={handlePublishSiteClick}
            isPublishing={isPublishing}
          />

          <LayoutsSection
            layouts={layouts}
            siteId={siteId!}
            isLoading={isLayoutsLoading}
            formatDate={formatDate}
            onDeleteClick={layout => {
              setLayoutToDelete(layout);
              setShowLayoutDeleteConfirm(true);
            }}
            onPublishLayout={handleQuickPublishLayout}
            publishingLayoutId={publishingLayoutId}
            layoutStatusMap={layoutStatusMap}
          />

          <PagesSection
            pages={pages}
            site={site}
            siteId={siteId!}
            sensors={sensors}
            isReordering={isReordering}
            activeId={activeId}
            activePage={activePage}
            formatDate={formatDate}
            onAddPageClick={handleAddPageClick}
            onAddPageWithRoaryClick={handleAddPageWithRoaryClick}
            showRoaryActions={showRoaryUi}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onEditPage={handleEditPageClick}
            onDeletePage={handleDeletePageClick}
            onToggleHomePage={handleToggleHomePage}
            onPublishPage={handleQuickPublishPage}
            publishingPageId={publishingPageId}
            pageStatusMap={pageStatusMap}
          />

          <AppsSection
            siteId={siteId!}
            blogData={blogData}
            isBlogLoading={isBlogLoading}
            isInstallingBlog={isInstallingBlog}
            onInstallBlog={handleInstallBlog}
            onUninstallClick={() => setShowBlogUninstallConfirm(true)}
          />
        </div>
      </main>

      {/* Modals */}
      <CreatePageModal
        isOpen={showCreatePageModal}
        onClose={() => setShowCreatePageModal(false)}
        onSubmit={handleCreatePage}
        onSubmitFromTemplate={handleCreatePageFromTemplate}
        isLoading={isCreatingPage}
        siteId={siteId}
      />

      {pageToEdit && (
        <EditPageModal
          isOpen={showEditPageModal}
          onClose={handleEditPageCancel}
          page={pageToEdit}
          onPageUpdated={handleEditPageConfirm}
        />
      )}

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeletingPage}
        title="Delete Page"
        message={`Are you sure you want to delete "${pageToDelete?.title}"? This action cannot be undone.`}
      />

      <DeleteConfirmModal
        isOpen={showLayoutDeleteConfirm}
        onClose={() => {
          setShowLayoutDeleteConfirm(false);
          setLayoutToDelete(null);
        }}
        onConfirm={handleDeleteLayout}
        isLoading={isDeletingLayout}
        title="Delete Layout"
        message={`Are you sure you want to delete the layout "${layoutToDelete?.name}"? Pages using this layout will no longer have it applied. This action cannot be undone.`}
      />

      <SaveSiteAsTemplateModal
        isOpen={showSaveTemplateModal}
        onClose={() => setShowSaveTemplateModal(false)}
        onSubmit={handleSaveAsTemplate}
        isLoading={isSavingTemplate}
        siteName={site?.name || ''}
        isAdmin={isAdmin}
      />

      <PublishConfirmModal
        isOpen={showPublishModal}
        onClose={handlePublishModalClose}
        onConfirm={handlePublishSiteConfirm}
        isPublished={site?.isPublished || false}
        siteName={site?.name || ''}
        isLoading={isPublishing}
        showRoaryBranding={showRoaryUi}
      />

      <DeleteConfirmModal
        isOpen={showBlogUninstallConfirm}
        onClose={() => setShowBlogUninstallConfirm(false)}
        onConfirm={handleUninstallBlog}
        isLoading={isUninstallingBlog}
        title="Uninstall Blog"
        message="Are you sure you want to uninstall the blog? This will permanently delete all blog posts, tags, and settings. This action cannot be undone."
      />

      <ThemeSelectorModal
        isOpen={showThemeSelector}
        onClose={() => setShowThemeSelector(false)}
        site={site}
        onSiteUpdate={setSite}
        onShowToast={showToast}
        onAfterUpdate={refetchSummary}
      />

      <SeoSettingsModal
        isOpen={showSeoModal}
        onClose={() => setShowSeoModal(false)}
        site={site}
        onSiteUpdate={setSite}
        onShowToast={showToast}
        onAfterUpdate={refetchSummary}
        onRequestPublishPrompt={() => setShowSeoPublishPrompt(true)}
      />

      <ConfirmModal
        isOpen={showSeoPublishPrompt}
        onClose={() => setShowSeoPublishPrompt(false)}
        onConfirm={handlePublishAll}
        type="info"
        title="Publish your changes?"
        message="Your SEO and analytics settings were saved. Publish All so Pixel, CAPI, and SEO updates appear on your live site."
        confirmText="Publish now"
        cancelText="I'll do it later"
        isLoading={isPublishingAll}
        loadingText="Publishing..."
      />

      <ToastNotification
        isVisible={showSuccessToast}
        message={successMessage}
        type={toastType}
        onClose={() => setShowSuccessToast(false)}
      />

      {pages.length > 0 && (
        <ResponsivePreviewPanel
          siteId={siteId!}
          pageId={
            (
              pages.find(p => p.id === site?.homePageId) ||
              pages.find(p => p.isHomePage) ||
              pages[0]
            ).id
          }
          isOpen={showResponsivePreview}
          onClose={() => setShowResponsivePreview(false)}
        />
      )}

      {siteId && (
        <SiteSEOOverviewPanel
          siteId={siteId}
          isOpen={showSEOOverview}
          onClose={() => setShowSEOOverview(false)}
          showRoaryAudit={showRoaryUi}
        />
      )}

      {/* Media Library Modal */}
      <MediaLibraryModal
        isOpen={showMediaLibrary}
        onClose={() => setShowMediaLibrary(false)}
        siteId={siteId}
      />

      {/* Brand Package Manager */}
      <BrandPackageManager
        isOpen={showBrandPackageModal}
        onClose={() => setShowBrandPackageModal(false)}
        onSelect={handleBrandPackageSelect}
        onBrandPackageEdited={refetchSummary}
        selectedBrandPackageId={(site as any)?.brandPackageId || null}
        mode="select"
      />

      {siteId && showRoaryUi && (
        <RoaryAddPageFlow
          isOpen={showRoaryAddPage}
          onClose={() => setShowRoaryAddPage(false)}
          siteId={siteId}
        />
      )}

      <GettingStartedHost sites={site ? [site] : undefined} />
    </div>
  );
};
